import React, { useState, useRef } from "react";
import { useFormikContext, Field } from "formik";
import {
  Grid,
  TextField,
  Box,
  Typography,
  FormLabel,
} from "@mui/material";
import { GoogleMap, Marker, useLoadScript, InfoWindow } from "@react-google-maps/api";

const AddressStep = () => {
  const { values, setFieldValue } = useFormikContext();
  const [addressInfo, setAddressInfo] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 19.432608, lng: -99.133209 }); // Default to CDMX
  const [mapZoom, setMapZoom] = useState(15);
  const autocompleteService = useRef(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCX6EAuN8_TMLRvuoaeadnLGo9KEDW0Fpo",
    libraries: ["places"],
  });

  // Manejo del cambio en el campo de autocompletado
  const handleAddressInfoChange = (event) => {
    setAddressInfo(event.target.value);

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (autocompleteService.current) {
      autocompleteService.current.getPlacePredictions(
        { input: event.target.value },
        (predictions) => {
          if (predictions) {
            setSuggestions(
              predictions.map((prediction) => ({
                description: prediction.description,
                placeId: prediction.place_id,
              }))
            );
          } else {
            setSuggestions([]);
          }
        }
      );
    }
  };

  // Selección de una sugerencia
  const handleSelectSuggestion = (suggestion) => {
    setAddressInfo(suggestion.description);
    setSuggestions([]);
    setFieldValue("address.full_address", suggestion.description);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: suggestion.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const location = results[0].geometry.location;
        const addressComponents = results[0].address_components;

        setMapCenter({ lat: location.lat(), lng: location.lng() });
        setMapZoom(18); // Ajusta el zoom al seleccionar una dirección

        // Actualiza los valores de Formik basándose en los componentes de dirección
        setFieldValue("address.partner_latitude", location.lat());
        setFieldValue("address.partner_longitude", location.lng());

        const street = getAddressComponent(addressComponents, "route");
        const city = getAddressComponent(addressComponents, "locality");
        const state = getAddressComponent(
          addressComponents,
          "administrative_area_level_1"
        );
        const zip = getAddressComponent(addressComponents, "postal_code");

        setFieldValue("address.street", street || "");
        setFieldValue("address.city", city || "");
        setFieldValue("address.state_id", state || "");
        setFieldValue("address.zip", zip || "");
      }
    });
  };

  // Geocodificador inverso para actualizar la dirección cuando se mueve el marcador
  const handleMarkerDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMapCenter({ lat, lng });
    setFieldValue("address.partner_latitude", lat);
    setFieldValue("address.partner_longitude", lng);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const addressComponents = results[0].address_components;
        const fullAddress = results[0].formatted_address;

        setFieldValue("address.full_address", fullAddress);

        const street = getAddressComponent(addressComponents, "route");
        const city = getAddressComponent(addressComponents, "locality");
        const state = getAddressComponent(
          addressComponents,
          "administrative_area_level_1"
        );
        const zip = getAddressComponent(addressComponents, "postal_code");

        setFieldValue("address.street", street || "");
        setFieldValue("address.city", city || "");
        setFieldValue("address.state_id", state || "");
        setFieldValue("address.zip", zip || "");
      }
    });
  };

  // Manejo del clic en el mapa
  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMapCenter({ lat, lng });
    setMapZoom(15);
    setFieldValue("address.partner_latitude", lat);
    setFieldValue("address.partner_longitude", lng);

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const addressComponents = results[0].address_components;
        const fullAddress = results[0].formatted_address;

        setFieldValue("address.full_address", fullAddress);

        const street = getAddressComponent(addressComponents, "route");
        const city = getAddressComponent(addressComponents, "locality");
        const state = getAddressComponent(
          addressComponents,
          "administrative_area_level_1"
        );
        const zip = getAddressComponent(addressComponents, "postal_code");

        setFieldValue("address.street", street || "");
        setFieldValue("address.city", city || "");
        setFieldValue("address.state_id", state || "");
        setFieldValue("address.zip", zip || "");
      }
    });
  };

  // Obtiene un componente de dirección específico
  const getAddressComponent = (components, type) => {
    const component = components.find((comp) =>
      comp.types.includes(type)
    );
    return component ? component.long_name : "";
  };

  return (
    <Grid container spacing={2}>
      {/* Campo de búsqueda de dirección */}
      <Grid item xs={12}>
        <FormLabel>Dirección</FormLabel>
        <TextField
          fullWidth
          name="address_info"
          onChange={handleAddressInfoChange}
          value={addressInfo}
          placeholder="Ingresa una dirección"
        />
        {suggestions.length > 0 && (
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 2,
              maxHeight: 150,
              overflowY: "auto",
              mt: 1,
            }}
          >
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                sx={{
                  padding: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion.description}
              </Box>
            ))}
          </Box>
        )}
      </Grid>

      {/* Mapa */}
      <Grid item xs={12}>
        {isLoaded ? (
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            mapContainerStyle={{ width: "100%", height: "400px" }}
            onClick={handleMapClick}
            onDrag={() => setShowInfoWindow(null)}
          >
            <Marker
              position={mapCenter}
              draggable={true}
              animation={window.google.maps.Animation.DROP} 
              icon={{
                url: "https://maps.google.com/mapfiles/kml/paddle/red-circle.png", // URL del ícono
                scaledSize: new window.google.maps.Size(30, 40), // Tamaño del icono
                anchor: new window.google.maps.Point(15, 0), // Punto de anclaje
              }}
              label={{
                text: "Arrastra para ajustar",
                color: "blue",
                fontWeight: "bold",
              }}
            />
                {showInfoWindow && (
                <InfoWindow
                    position={mapCenter}
                    onCloseClick={() => setShowInfoWindow(false)}
                >
                    <Box>
                    <Typography variant="subtitle1">Ubicación seleccionada</Typography>
                    <Typography variant="body2">
                        Latitud: {mapCenter.lat.toFixed(6)}
                        <br />
                        Longitud: {mapCenter.lng.toFixed(6)}
                    </Typography>
                    </Box>
                </InfoWindow>
                )}
          </GoogleMap>
        ) : (
          <Typography>Cargando mapa...</Typography>
        )}
      </Grid>

      {/* Campos del formulario */}
      <Grid item xs={12} sm={6}>
        <Field name="address.street">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Calle"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field name="address.interior_number">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Número Interior"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field name="address.city">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Ciudad"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field name="address.state">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Estado"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field name="address.zip">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Código Postal"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field name="address.reference">
          {({ field, meta }) => (
            <TextField
              {...field}
              label="Referencia"
              fullWidth
              error={meta.touched && !!meta.error}
              helperText={meta.touched && meta.error}
              variant="outlined"
            />
          )}
        </Field>
      </Grid>
    </Grid>
  );
};

export default AddressStep;
